'use client';

import { c as _c } from "react/compiler-runtime";
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { type Provider, createContext, use, useCallback, useMemo, useSyncExternalStore } from 'react';
import { useLocale } from 'react-aria';
type EmblaApi = NonNullable<UseEmblaCarouselType[1]>;

/** Alignment of each selected slide within the viewport. */
export type CarouselAlignment = 'start' | 'center' | 'end';

/** Pointer and touch dragging behaviour. */
export type CarouselDragMode = 'snap' | 'momentum' | 'none';

/** When pointer and touch dragging should be available. */
export type CarouselDragAvailability = 'overflow' | 'always';

/** The keyboard-access strategy selected by a carousel consumer. */
export type CarouselKeyboardNavigation = 'controls' | 'focusable-slides';

/** Scroll axis of the carousel. */
export type CarouselOrientation = 'horizontal' | 'vertical';

/**
 * Configuration accepted by {@link useCarouselState} and, by extension, the
 * `Carousel` root component.
 */
export type CarouselStateOptions = {
  /** Slide alignment. Defaults to `'start'`. */
  alignment?: CarouselAlignment;
  /** Enable dragging only for overflow, or always. Defaults to `'overflow'`. */
  dragAvailability?: CarouselDragAvailability;
  /** Drag behaviour. Defaults to `'snap'`. */
  dragMode?: CarouselDragMode;
  /** Initially selected slide. Defaults to `0`. */
  initialSlide?: number;
  /** Required keyboard-access strategy. */
  keyboardNavigation: CarouselKeyboardNavigation;
  /** Whether navigation wraps at either end. Defaults to `false`. */
  loop?: boolean;
  /** Scroll axis. Defaults to `'horizontal'`. */
  orientation?: CarouselOrientation;
};

/**
 * Stable carousel state exposed to consumers through {@link useCarousel}.
 */
export type CarouselState = {
  /** Whether there is a next slide to scroll to. */
  canScrollNext: boolean;
  /** Whether there is a previous slide to scroll to. */
  canScrollPrevious: boolean;
  /** The resolved scroll axis. */
  orientation: CarouselOrientation;
  /** Scroll to the next slide (no-op until the carousel is initialized). */
  scrollNext: () => void;
  /** Scroll to the previous slide (no-op until the carousel is initialized). */
  scrollPrevious: () => void;
  /** Index of the currently selected slide. */
  selectedIndex: number;
  /** Total number of slides. */
  slideCount: number;
};
type CarouselContextValue = CarouselState & {
  /** The private Embla API, unavailable through the public hook. */
  emblaApi: EmblaApi | undefined;
  /** Whether off-screen slides should be removed from sequential navigation. */
  inertHiddenSlides: boolean;
  /** Callback ref to attach to the scroll viewport element. */
  viewportRef: ReturnType<typeof useEmblaCarousel>[0];
};
const CarouselContext = createContext<CarouselContextValue | null>(null);

/**
 * Context provider for carousel state. Rendered by the `Carousel` root; not
 * intended for direct use by consumers.
 */
export const CarouselProvider: Provider<CarouselContextValue> = CarouselContext.Provider;
const hasScrollableOverflow = (emblaApi: EmblaApi): boolean => emblaApi.canScrollPrev() || emblaApi.canScrollNext();
const getWatchDrag = (dragMode: CarouselDragMode, dragAvailability: CarouselDragAvailability): boolean | typeof hasScrollableOverflow => {
  if (dragMode === 'none') {
    return false;
  }
  if (dragAvailability === 'always') {
    return true;
  }
  return hasScrollableOverflow;
};

/**
 * Creates the internal carousel state. Primitive snapshots keep
 * `useSyncExternalStore` identity comparisons stable.
 */
export const useCarouselState = t0 => {
  const $ = _c(40);
  const {
    alignment: t1,
    dragAvailability: t2,
    dragMode: t3,
    initialSlide: t4,
    keyboardNavigation,
    loop: t5,
    orientation: t6
  } = t0;
  const alignment = t1 === undefined ? "start" : t1;
  const dragAvailability = t2 === undefined ? "overflow" : t2;
  const dragMode = t3 === undefined ? "snap" : t3;
  const initialSlide = t4 === undefined ? 0 : t4;
  const loop = t5 === undefined ? false : t5;
  const orientation = t6 === undefined ? "horizontal" : t6;
  const {
    direction
  } = useLocale();
  const t7 = orientation === "horizontal" ? "x" : "y";
  const t8 = dragMode === "momentum";
  let t9;
  if ($[0] !== dragAvailability || $[1] !== dragMode) {
    t9 = getWatchDrag(dragMode, dragAvailability);
    $[0] = dragAvailability;
    $[1] = dragMode;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const t10 = keyboardNavigation === "focusable-slides";
  let t11;
  if ($[3] !== alignment || $[4] !== direction || $[5] !== initialSlide || $[6] !== loop || $[7] !== t10 || $[8] !== t7 || $[9] !== t8 || $[10] !== t9) {
    t11 = {
      align: alignment,
      axis: t7,
      containScroll: "trimSnaps",
      direction,
      dragFree: t8,
      loop,
      startIndex: initialSlide,
      watchDrag: t9,
      watchFocus: t10
    };
    $[3] = alignment;
    $[4] = direction;
    $[5] = initialSlide;
    $[6] = loop;
    $[7] = t10;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const [viewportRef, emblaApi] = useEmblaCarousel(t11);
  const inertHiddenSlides = keyboardNavigation === "controls";
  let t12;
  if ($[12] !== emblaApi) {
    t12 = onStoreChange => {
      if (!emblaApi) {
        return _temp;
      }
      emblaApi.on("reInit", onStoreChange);
      emblaApi.on("select", onStoreChange);
      return () => {
        emblaApi.off("reInit", onStoreChange);
        emblaApi.off("select", onStoreChange);
      };
    };
    $[12] = emblaApi;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const subscribe = t12;
  let t13;
  if ($[14] !== emblaApi) {
    t13 = () => emblaApi?.canScrollPrev() ?? false;
    $[14] = emblaApi;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const getCanScrollPrevious = t13;
  let t14;
  if ($[16] !== emblaApi) {
    t14 = () => emblaApi?.canScrollNext() ?? false;
    $[16] = emblaApi;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const getCanScrollNext = t14;
  let t15;
  if ($[18] !== emblaApi || $[19] !== initialSlide) {
    t15 = () => emblaApi?.selectedScrollSnap() ?? initialSlide;
    $[18] = emblaApi;
    $[19] = initialSlide;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const getSelectedIndex = t15;
  let t16;
  if ($[21] !== emblaApi) {
    t16 = () => emblaApi?.slideNodes().length ?? 0;
    $[21] = emblaApi;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const getSlideCount = t16;
  const getServerBooleanSnapshot = _temp2;
  const getServerNumberSnapshot = _temp3;
  let t17;
  if ($[23] !== initialSlide) {
    t17 = () => initialSlide;
    $[23] = initialSlide;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const getServerSelectedIndexSnapshot = t17;
  const canScrollPrevious = useSyncExternalStore(subscribe, getCanScrollPrevious, getServerBooleanSnapshot);
  const canScrollNext = useSyncExternalStore(subscribe, getCanScrollNext, getServerBooleanSnapshot);
  const selectedIndex = useSyncExternalStore(subscribe, getSelectedIndex, getServerSelectedIndexSnapshot);
  const slideCount = useSyncExternalStore(subscribe, getSlideCount, getServerNumberSnapshot);
  let t18;
  if ($[25] !== emblaApi) {
    t18 = () => {
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollPrev();
    };
    $[25] = emblaApi;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const scrollPrevious = t18;
  let t19;
  if ($[27] !== emblaApi) {
    t19 = () => {
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollNext();
    };
    $[27] = emblaApi;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const scrollNext = t19;
  let t20;
  if ($[29] !== canScrollNext || $[30] !== canScrollPrevious || $[31] !== emblaApi || $[32] !== inertHiddenSlides || $[33] !== orientation || $[34] !== scrollNext || $[35] !== scrollPrevious || $[36] !== selectedIndex || $[37] !== slideCount || $[38] !== viewportRef) {
    t20 = {
      canScrollNext,
      canScrollPrevious,
      emblaApi,
      inertHiddenSlides,
      orientation,
      scrollNext,
      scrollPrevious,
      selectedIndex,
      slideCount,
      viewportRef
    };
    $[29] = canScrollNext;
    $[30] = canScrollPrevious;
    $[31] = emblaApi;
    $[32] = inertHiddenSlides;
    $[33] = orientation;
    $[34] = scrollNext;
    $[35] = scrollPrevious;
    $[36] = selectedIndex;
    $[37] = slideCount;
    $[38] = viewportRef;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  return t20;
};

/** @internal Reads the private carousel context for primitive composition. */
export const useCarouselInternalContext = (): CarouselContextValue => {
  const context = use(CarouselContext);
  if (!context) {
    throw new Error('Carousel components and hooks must be used within a <Carousel>');
  }
  return context;
};

/**
 * Reads stable Ark carousel state and navigation actions.
 *
 * Use this inside composed controls or status UI. The underlying carousel
 * implementation is intentionally not exposed.
 *
 * @throws If called outside of a `Carousel`.
 */
export const useCarousel = () => {
  const $ = _c(8);
  const {
    canScrollNext,
    canScrollPrevious,
    orientation,
    scrollNext,
    scrollPrevious,
    selectedIndex,
    slideCount
  } = useCarouselInternalContext();
  let t0;
  if ($[0] !== canScrollNext || $[1] !== canScrollPrevious || $[2] !== orientation || $[3] !== scrollNext || $[4] !== scrollPrevious || $[5] !== selectedIndex || $[6] !== slideCount) {
    t0 = {
      canScrollNext,
      canScrollPrevious,
      orientation,
      scrollNext,
      scrollPrevious,
      selectedIndex,
      slideCount
    };
    $[0] = canScrollNext;
    $[1] = canScrollPrevious;
    $[2] = orientation;
    $[3] = scrollNext;
    $[4] = scrollPrevious;
    $[5] = selectedIndex;
    $[6] = slideCount;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return t0;
};

/**
 * Reports whether a slide is in view. A boolean snapshot avoids unstable array
 * identity and defaults to `true` before measurement to preserve reachability.
 */
export const useSlideInView = index => {
  const $ = _c(5);
  const {
    emblaApi
  } = useCarouselInternalContext();
  let t0;
  if ($[0] !== emblaApi) {
    t0 = onStoreChange => {
      if (!emblaApi) {
        return _temp4;
      }
      emblaApi.on("slidesInView", onStoreChange);
      emblaApi.on("reInit", onStoreChange);
      return () => {
        emblaApi.off("slidesInView", onStoreChange);
        emblaApi.off("reInit", onStoreChange);
      };
    };
    $[0] = emblaApi;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const subscribe = t0;
  let t1;
  if ($[2] !== emblaApi || $[3] !== index) {
    t1 = () => emblaApi ? emblaApi.slidesInView().includes(index) : true;
    $[2] = emblaApi;
    $[3] = index;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const getSnapshot = t1;
  const getServerSnapshot = _temp5;
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
function _temp() {}
function _temp2() {
  return false;
}
function _temp3() {
  return 0;
}
function _temp4() {}
function _temp5() {
  return true;
}